import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";

import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../hooks/use-auth";
import { useToast } from "../hooks/use-toast";
import { Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { sendPasswordReset } from "../lib/data";

export default function LoginForm() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { toast } = useToast();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetEmailError, setResetEmailError] = useState("");

  // Spaces around the email don't count: phone keyboards add one after a
  // suggestion or autofill, and the email was then "not valid" before the
  // password was even checked (login() trims it anyway).
  const validateEmail = (email: string) => {
    const value = email.trim();
    if (!value) return "Email je obavezan.";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Molimo unesite valjanu email adresu.";
    return "";
  };

  const validatePassword = (password: string) => {
    if (!password) return "Lozinka je obavezna.";
    return "";
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Clear previous errors
    setEmailError("");
    setPasswordError("");

    // Validate
    const emailErr = validateEmail(email);
    const passwordErr = validatePassword(password);

    if (emailErr || passwordErr) {
      setEmailError(emailErr);
      setPasswordError(passwordErr);
      return;
    }

    setIsSubmitting(true);
    const result = await login(email, password);
    if (result.ok) {
      navigate("/feed");
      toast({
        file: "components/login-form.tsx", code: "NN-SUC-015",
        title: "Prijava uspješna",
        description: "Dobrodošli natrag!",
      });
    } else if (result.reason === "no-account") {
      toast({
        file: "components/login-form.tsx", code: "NN-ERR-212",
        variant: "destructive",
        title: "Račun nije pronađen",
        description: "Za ovaj email ne postoji račun u aplikaciji. Obratite se svom treneru.",
      });
    } else if (result.reason === "network") {
      toast({
        file: "components/login-form.tsx", code: "NN-ERR-213",
        variant: "destructive",
        title: "Nema veze",
        description: "Prijava nije uspjela zbog internetske veze. Pokušajte ponovno.",
      });
    } else {
      toast({
        file: "components/login-form.tsx", code: "NN-VAL-016",
        variant: "destructive",
        title: "Prijava neuspješna",
        description: "Neispravan email ili lozinka. Molimo pokušajte ponovno.",
      });
    }
    setIsSubmitting(false);
  }

  const handlePasswordReset = async () => {
    setResetEmailError("");

    const emailErr = validateEmail(resetEmail);
    if (emailErr) {
      setResetEmailError(emailErr);
      return;
    }

    setIsSendingReset(true);
    try {
      await sendPasswordReset(resetEmail.trim());
      toast({
        file: "components/login-form.tsx", code: "NN-SUC-017",
        title: "Email za ponovno postavljanje poslan",
        description: "Provjerite svoj sandučić za daljnje upute.",
      });
      setIsResetDialogOpen(false);
      setResetEmail("");
      setResetEmailError("");
    } catch (error: any) {
      toast({
        file: "components/login-form.tsx", code: "NN-ERR-018",
        variant: "destructive",
        title: "Greška",
        description:
          "Nije moguće poslati email za ponovno postavljanje. Molimo provjerite je li email ispravan.",
      });
    } finally {
      setIsSendingReset(false);
    }
  };

  return (
    <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">Prijava</CardTitle>
          <CardDescription>
            Unesite svoj email ispod za prijavu na svoj račun.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} noValidate className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder="ime@primjer.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={emailError ? "border-destructive" : ""}
              />
              {emailError && (
                <p className="text-sm text-destructive">{emailError}</p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center">
                <Label htmlFor="password">Lozinka</Label>
                <DialogTrigger asChild>
                  <button
                    type="button"
                    className="ml-auto inline-block text-sm underline"
                  >
                    Zaboravili ste lozinku?
                  </button>
                </DialogTrigger>
              </div>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                autoCapitalize="none"
                autoCorrect="off"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={passwordError ? "border-destructive" : ""}
              />
              {passwordError && (
                <p className="text-sm text-destructive">{passwordError}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-accent hover:bg-[#e73e7b]"
              disabled={isSubmitting}
            >
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Prijavi se
            </Button>
          </form>
          <div className="w-full flex justify-center mt-3">
            <Link to="/activate-account" className="text-sm underline">
              Nisi dobio mail za aktivaciju računa?
            </Link>
          </div>
        </CardContent>
      </Card>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ponovno postavljanje lozinke</DialogTitle>
          <DialogDescription>
            Unesite svoju email adresu da biste primili link za ponovno
            postavljanje lozinke.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-4">
          <Label htmlFor="reset-email">Email</Label>
          <Input
            id="reset-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="ime@primjer.com"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            disabled={isSendingReset}
            className={resetEmailError ? "border-destructive" : ""}
          />
          {resetEmailError && (
            <p className="text-sm text-destructive">{resetEmailError}</p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsResetDialogOpen(false)}
            className="hover:bg-[#e73e7b]"
          >
            Otkaži
          </Button>
          <Button
            onClick={handlePasswordReset}
            disabled={isSendingReset}
            className="bg-accent hover:bg-[#e73e7b]"
          >
            {isSendingReset && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Pošalji link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
